/**
 * The two pictures the owner chose for Sales Rep Performance (2026-10-17):
 *
 * * **Bullet bars** -- every rep, the month and the fiscal year to date side by
 *   side at full width. The grey band is the budget, the bar is actual (green
 *   once at or over budget), the orange tick is last year, and the label is
 *   actual as a percentage of budget.
 * * **The bridge** -- for one rep, from their FYTD budget to their FYTD actual,
 *   one step per end user (their top 10, then All others), so what made up the
 *   gap reads left to right.
 *
 * Both use only bar and line series, which the shared chart registers.
 */

import type { EChartsOption } from 'echarts'

import { Empty } from '../shared/Card'
import { type Keyed, text } from '../shared/rows'
import { axisFormatter, COLOURS, GRID, LEGEND_TOP, num, rankSize, type Unit } from './format'
import { ChartFrame } from './parts'

type Period = 'month' | 'fytd'

function bulletOption(
  rows: readonly Keyed[],
  period: Period,
  unit: Unit,
  keyColumn: string,
): EChartsOption {
  const value = (row: Keyed, key: string) => num(row[`${period}_${key}`]) ?? 0
  const actual = rows.map((row) => value(row, 'actual'))
  const budget = rows.map((row) => value(row, 'budget'))
  const prior = rows.map((row) => value(row, 'py'))
  const names = rows.map((row) => text(row[keyColumn]) ?? '')
  const pct = (i: number) => {
    const b = budget[i] ?? 0
    return b > 0 ? `${String(Math.round(((actual[i] ?? 0) / b) * 100))}%` : ''
  }
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (v) => (typeof v === 'number' ? Math.round(v).toLocaleString('en-US') : String(v)),
    },
    legend: { ...LEGEND_TOP, data: ['Actual', 'Budget', 'Prior year'] },
    grid: { ...GRID, right: 44 },
    xAxis: { type: 'value', axisLabel: { formatter: axisFormatter(unit), hideOverlap: true } },
    // The budget band on a hidden copy of the axis, centred behind the bar.
    yAxis: [
      {
        type: 'category',
        data: names,
        axisTick: { show: false },
        axisLabel: { width: 150, overflow: 'truncate' },
      },
      { type: 'category', data: names, show: false },
    ],
    series: [
      {
        name: 'Budget',
        type: 'bar',
        yAxisIndex: 1,
        data: budget,
        barWidth: 18,
        itemStyle: { color: COLOURS.budget, opacity: 0.5 },
        z: 1,
      },
      {
        name: 'Actual',
        type: 'bar',
        data: actual.map((v, i) => ({
          value: v,
          itemStyle: { color: v >= (budget[i] ?? 0) && (budget[i] ?? 0) > 0 ? COLOURS.over : COLOURS.actual },
        })),
        barWidth: 7,
        z: 3,
        label: {
          show: true,
          position: 'right',
          distance: 6,
          fontSize: 11,
          formatter: (p) => pct(p.dataIndex),
        },
      },
      {
        // A line series drawn as ticks only: the shared chart registers no
        // scatter, and a rectangle symbol on an invisible line is the same mark.
        name: 'Prior year',
        type: 'line',
        data: prior,
        symbol: 'rect',
        symbolSize: [3, 20],
        lineStyle: { opacity: 0 },
        itemStyle: { color: COLOURS.py },
        z: 4,
      },
    ],
  }
}

function bulletHeight(count: number): number {
  return Math.max(240, count * 34 + 60)
}

/** One bullet chart: the month or the fiscal year to date. */
export function Bullets({
  rows,
  unit,
  period,
  title,
  label,
  keyColumn = 'sales_rep',
}: {
  rows: readonly Keyed[]
  unit: Unit
  period: Period
  title: string
  label: string
  keyColumn?: string
}) {
  return (
    <div className="srp-chart">
      <p className="srp-chart-title">{title}</p>
      <ChartFrame
        option={bulletOption(rows, period, unit, keyColumn)}
        label={label}
        height={bulletHeight(rows.length)}
      />
    </div>
  )
}

/**
 * The month and FYTD side by side, at full width -- every rep, or one rep's
 * customers (`keyColumn`). Largest FYTD at the top.
 *
 * In a month still in progress the month half is the wrong picture -- half a
 * month's actual against a whole month's budget always reads short -- so the
 * caller passes `progress` instead: shipped, booked and the forecast's
 * projected remainder against the budget (owner, 2026-10-18).
 */
export function BulletPair({
  rows,
  unit,
  monthLabel,
  keyColumn = 'sales_rep',
  noun = 'rep',
  progress,
}: {
  rows: readonly Keyed[]
  unit: Unit
  monthLabel: string
  keyColumn?: string
  noun?: string
  progress?: readonly Keyed[] | undefined
}) {
  const ordered = [...rows].sort((a, b) => (num(a['fytd_actual']) ?? 0) - (num(b['fytd_actual']) ?? 0))
  return (
    <div className="srp-charts srp-charts-even">
      {progress === undefined ? (
        <Bullets
          rows={ordered}
          unit={unit}
          period="month"
          title={monthLabel}
          label={`${monthLabel} by ${noun}`}
          keyColumn={keyColumn}
        />
      ) : (
        <ProgressBullets
          rows={progress}
          unit={unit}
          title={`${monthLabel} so far, and where it is heading`}
          label={`${monthLabel} progress by ${noun}`}
          keyColumn={keyColumn}
        />
      )}
      <Bullets
        rows={ordered}
        unit={unit}
        period="fytd"
        title={progress === undefined ? 'Fiscal year to date' : 'Fiscal year to date, with this month’s forecast'}
        label={`Fiscal year to date by ${noun}`}
        keyColumn={keyColumn}
      />
    </div>
  )
}

/**
 * The month in progress: the solid bar is what has shipped, the next segment
 * what is booked, the pale segment what the forecast still expects (A3X,
 * without Janus) -- the bar's end is the forecast -- and a dark vertical tick
 * marks the budget. The label is the forecast as a percentage of budget. Rows carry `shipped`,
 * `booked`, `projected`, `month_end` and `budget` (the `fc_*` datasets).
 */
export function ProgressBullets({
  rows,
  unit,
  title,
  label,
  keyColumn = 'sales_rep',
  planName = 'budget',
}: {
  rows: readonly Keyed[]
  unit: Unit
  title: string
  label: string
  keyColumn?: string
  /** What the tick is, in the legend and the label: "budget" or "plan". */
  planName?: string
}) {
  const Plan = planName.charAt(0).toUpperCase() + planName.slice(1)
  const ordered = [...rows].sort(
    (a, b) => (num(a['month_end']) ?? 0) - (num(b['month_end']) ?? 0),
  )
  const col = (key: string) => ordered.map((row) => Math.max(0, num(row[key]) ?? 0))
  const budget = col('budget')
  const ends = ordered.map((row) => num(row['month_end']) ?? 0)
  const pct = (i: number) => {
    const b = budget[i] ?? 0
    return b > 0 ? `${String(Math.round(((ends[i] ?? 0) / b) * 100))}%` : ''
  }
  const segment = (name: string, key: string, color: string, last: boolean) => ({
    name,
    type: 'bar' as const,
    stack: 'progress',
    data: col(key),
    barWidth: 14,
    itemStyle: { color },
    z: 3,
    ...(last
      ? {
          label: {
            show: true,
            position: 'right' as const,
            distance: 6,
            fontSize: 11,
            formatter: (p: { dataIndex: number }) => pct(p.dataIndex) && `${pct(p.dataIndex)} of ${planName}`,
          },
        }
      : {}),
  })
  const option: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (v) => (typeof v === 'number' ? Math.round(v).toLocaleString('en-US') : String(v)),
    },
    legend: { ...LEGEND_TOP, data: ['Shipped', 'Booked', 'Still expected', Plan] },
    grid: { ...GRID, right: 100 },
    xAxis: { type: 'value', axisLabel: { formatter: axisFormatter(unit), hideOverlap: true } },
    // A hidden copy of the category axis carries the labels' transparent bar,
    // so it overlays its row rather than taking a slot beside the bar.
    yAxis: [
      {
        type: 'category',
        data: ordered.map((row) => text(row[keyColumn]) ?? ''),
        axisTick: { show: false },
        axisLabel: { width: 150, overflow: 'truncate' },
      },
      { type: 'category', data: ordered.map((row) => text(row[keyColumn]) ?? ''), show: false },
    ],
    series: [
      segment('Shipped', 'shipped', COLOURS.actual, false),
      segment('Booked', 'booked', '#6E9BD1', false),
      segment('Still expected', 'projected', '#C9D8EC', false),
      {
        // The budget as a vertical tick across each bar (owner, 2026-10-18: "I
        // can't figure out what is the forecast vs budget"): a line series
        // drawn as markers only, so the bar is the forecast and the tick is
        // where it needs to reach.
        name: Plan,
        type: 'line',
        data: budget.map((v) => (v > 0 ? v : null)),
        symbol: 'rect',
        symbolSize: [3, 24],
        lineStyle: { opacity: 0 },
        itemStyle: { color: '#1F2933' },
        z: 5,
      },
      {
        // The label, past whichever ends further right -- the bar or the
        // budget tick -- so the two never sit on top of each other.
        name: 'label',
        type: 'bar',
        yAxisIndex: 1,
        data: ordered.map((_row, i) => Math.max(ends[i] ?? 0, budget[i] ?? 0)),
        barWidth: 1,
        itemStyle: { color: 'transparent' },
        silent: true,
        tooltip: { show: false },
        label: {
          show: true,
          position: 'right',
          distance: 8,
          fontSize: 11,
          color: '#33414E',
          formatter: (p: { dataIndex: number }) => pct(p.dataIndex) && `${pct(p.dataIndex)} of ${planName}`,
        },
        z: 0,
      },
    ],
  }
  return (
    <div className="srp-chart">
      <p className="srp-chart-title">{title}</p>
      <ChartFrame option={option} label={label} height={bulletHeight(ordered.length)} />
    </div>
  )
}

interface Step {
  readonly name: string
  readonly value: number
}

/** One rep's FYTD budget to FYTD actual, a step per end user. */
export function RepBridge({
  rows,
  unit,
  top = 10,
  title,
}: {
  rows: readonly Keyed[]
  unit: Unit
  top?: number
  title?: string | undefined
}) {
  if (rows.length === 0) return <Empty label="No end users for this rep." />
  const ranked = [...rows].sort((a, b) => rankSize(b) - rankSize(a))
  const head = ranked.length <= top + 1 ? ranked : ranked.slice(0, top)
  const rest = ranked.slice(head.length)
  const variance = (row: Keyed) => (num(row['fytd_actual']) ?? 0) - (num(row['fytd_budget']) ?? 0)
  const steps: Step[] = head.map((row) => ({ name: text(row['end_user']) ?? '—', value: variance(row) }))
  if (rest.length > 0) {
    steps.push({ name: `All others (${String(rest.length)})`, value: rest.reduce((sum, row) => sum + variance(row), 0) })
  }
  // Largest gains first, then the losses, so the bar falls left to right
  // through what helped and then what hurt.
  steps.sort((a, b) => b.value - a.value)
  const budget = rows.reduce((sum, row) => sum + (num(row['fytd_budget']) ?? 0), 0)
  const actual = rows.reduce((sum, row) => sum + (num(row['fytd_actual']) ?? 0), 0)

  const names = ['FYTD budget', ...steps.map((s) => s.name), 'FYTD actual']
  const base: (number | '-')[] = [0]
  const up: (number | '-')[] = [budget]
  const down: (number | '-')[] = ['-']
  let level = budget
  for (const step of steps) {
    if (step.value >= 0) {
      base.push(level)
      up.push(step.value)
      down.push('-')
      level += step.value
    } else {
      level += step.value
      base.push(level)
      up.push('-')
      down.push(-step.value)
    }
  }
  base.push(0)
  up.push(actual)
  down.push('-')
  const lastIndex = names.length - 1
  const isTotal = (i: number) => i === 0 || i === lastIndex
  // A step under 0.5% of the larger total gets no label: at that size the
  // labels collide, and the tooltip still gives the figure.
  const quiet = Math.max(budget, actual) * 0.005
  const low = Math.min(budget, actual, ...base.filter((v): v is number => typeof v === 'number' && v > 0))
  const short = axisFormatter(unit)

  const option: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const list = Array.isArray(params) ? params : [params]
        const i = list[0]?.dataIndex ?? 0
        if (isTotal(i)) return `<b>${names[i] ?? ''}</b><br>${Math.round(i === 0 ? budget : actual).toLocaleString('en-US')}`
        const step = steps[i - 1]
        if (step === undefined) return ''
        return `<b>${step.name}</b><br>${step.value >= 0 ? '+' : '−'}${Math.round(Math.abs(step.value)).toLocaleString('en-US')} vs budget`
      },
    },
    grid: { ...GRID, top: 24, bottom: 8 },
    xAxis: {
      type: 'category',
      data: names,
      axisTick: { show: false },
      axisLabel: { interval: 0, rotate: 30, width: 110, overflow: 'truncate', fontSize: 11 },
    },
    yAxis: {
      type: 'value',
      // Start near the lowest level so the steps are visible against two
      // totals many times their size.
      min: Math.max(0, Math.floor((low * 0.85) / 10 ** Math.floor(Math.log10(low || 1))) * 10 ** Math.floor(Math.log10(low || 1))),
      axisLabel: { formatter: short },
    },
    series: [
      { type: 'bar', stack: 'bridge', data: base, itemStyle: { color: 'transparent' }, silent: true, tooltip: { show: false } },
      {
        type: 'bar',
        stack: 'bridge',
        data: up.map((v, i) => (v === '-' ? v : { value: v, itemStyle: { color: isTotal(i) ? COLOURS.actual : COLOURS.over } })),
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          formatter: (p) => {
            const v = Number(p.value)
            if (isTotal(p.dataIndex)) return short(v)
            return v < quiet ? '' : `+${short(v)}`
          },
        },
      },
      {
        type: 'bar',
        stack: 'bridge',
        data: down,
        itemStyle: { color: COLOURS.under },
        label: {
          show: true,
          position: 'bottom',
          fontSize: 10,
          formatter: (p) => (Number(p.value) < quiet ? '' : `−${short(Number(p.value))}`),
        },
      },
    ],
  }
  return (
    <div className="srp-chart">
      <p className="srp-chart-title">{title ?? 'What made up the gap to budget, fiscal year to date'}</p>
      <ChartFrame option={option} label="Budget to actual bridge by end user" height={360} />
    </div>
  )
}
